import { SearchInput } from '@components/SearchInput';
import { Table } from '@components/Table';
import { Button, Stack, SxProps, Typography } from '@mui/material';
import {
  generationTaskApiSlice,
  IBulkOrderBody,
  notificationApiSlice,
  profileApiSlice,
  selectionsApiSlice,
} from '@src/api';
import { PAGE_SIZE } from '@src/api/common';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { B2BNotificationData, B2CNotificationData, RowSelection } from './RowSelection';
import { useTableHeader } from './hooks/useTableHeader';
import { useDebounce } from '@src/hooks/useDebounce';
import { border, buildDataTestIdAttribute, buildFullDataTestId, log } from '@src/utils';
import { EmptySearch } from '@components/Table/Table';
import { ReactComponent as PlusIcon } from '@icons/plus_18.svg';
import { AttentionModal } from '@components/Modal';
import { PortalContext } from '@src/context/portal';
import { RoleKeeper } from '@components/RoleKeeper';
import { SegmentedControl } from '@components/SegmentedControl';
import { getIconStyles, IconBaseColor } from '@src/utils/getIconStyles';
import { B2bNotificationModal, B2cNotificationModal, ConfirmOrdersCreate, OrdersCreateModal } from './components';
import { LoadingFormProvider } from './components/OrdersCreateModal/components';

const B2C_NOTIFICATIONS_PERMISSION = 'notification.patient.create';
const B2B_NOTIFICATIONS_PERMISSION = 'notification.orderingprovider.create';

const POLLING_INTERVAL_VALUE = 15_000;

const defaultFilterValues = {
  search: '',
  skip: 0,
  take: PAGE_SIZE,
};

const belongingValues = [
  { name: 'All', value: 'All' },
  { name: 'Mine', value: 'Mine' },
  { name: 'From labs', value: 'FromLabs' },
];

type FilterState = {
  search: string;
  skip: number;
  take: number;
  professionalSelectionListSourceType?: string;
  orderBy?: [
    {
      orderBy: string;
      orderDesc: boolean;
    },
  ];
};
export const Selections = () => {
  const navigate = useNavigate();
  const { module } = useContext(PortalContext);

  const addSelection = () => {
    navigate('new-selection');
  };

  const { data: profile } = profileApiSlice.useGetProfileQuery('');
  const { data: canCreateOrders } = selectionsApiSlice.useCanCreateBulkQuery('', { skip: module === 'LIS' });

  const canCreateB2CNotification =
    profile?.permissions.includes(B2C_NOTIFICATIONS_PERMISSION) && module === 'PROFESSIONAL';
  const canCreateB2BNotification = profile?.permissions.includes(B2B_NOTIFICATIONS_PERMISSION) && module === 'LIS';

  const [generationTask] = generationTaskApiSlice.useLazyGenerationTaskQuery();
  const [confirmOrdersCreate, setConfirmOrdersCreate] = useState<Nullable<{ data: IBulkOrderBody; id: number }>>(null);

  const [openGenerateModal, setOpenGenerateModal] = useState<Nullable<{ name: string; id: number }>>(null);
  const [openCreateOrder, setOpenCreateOrder] = useState<Nullable<number>>(null);
  const [filter, setFilter] = useState<FilterState>({
    ...defaultFilterValues,
    ...(module === 'PROFESSIONAL' ? { professionalSelectionListSourceType: 'All' } : {}),
  });
  const [pollingInterval, setPollingInterval] = useState(POLLING_INTERVAL_VALUE);

  const [b2cNotificationData, setB2cNotificationData] = useState<Nullable<B2CNotificationData>>(null);
  const [b2bNotificationData, setB2bNotificationData] = useState<Nullable<B2BNotificationData>>(null);

  const debouncedFilter = useDebounce<FilterState>(filter);

  useEffect(() => {
    setPollingInterval(POLLING_INTERVAL_VALUE);
    return () => setPollingInterval(0);
  }, []);

  const { data: selections, isFetching } = selectionsApiSlice.useGetSelectionsQuery(debouncedFilter, {
    refetchOnMountOrArgChange: true,
  });
  const { data: updatedSelections } = selectionsApiSlice.useRefreshForGenerationInfoQuery(
    selections?.data?.map(({ id }) => id) || [],
    { skip: !selections, pollingInterval: pollingInterval },
  );

  const { data: activeNotificationsIds } = notificationApiSlice.useGetActivePatientNotificationsQuery(
    (selections?.data || []).map(({ id }) => id),
    { skip: !selections },
  );

  const resultSelections = useMemo(
    () =>
      selections?.data
        ? selections?.data?.map(selection => {
            if (updatedSelections?.some(({ id }) => id === selection.id)) {
              const newSelectionValue = updatedSelections.find(({ id }) => id === selection.id);
              return newSelectionValue || selection;
            } else return selection;
          })
        : [],
    [selections, updatedSelections],
  );

  const onSearch = (value: string) => {
    if (value.length > 1 || value === '') setFilter(prev => ({ ...prev, skip: 0, search: value }));
  };

  const changeOrder = useCallback(
    (columnName: string) => () => {
      const newState: FilterState = {
        ...defaultFilterValues,
        search: filter.search,
        professionalSelectionListSourceType: filter?.professionalSelectionListSourceType || undefined,
        orderBy: [
          {
            orderBy: columnName,
            orderDesc: columnName === filter.orderBy?.[0].orderBy ? !filter.orderBy?.[0].orderDesc : true,
          },
        ],
      };
      setFilter(newState);
    },
    [filter],
  );

  const scrollRest = useCallback(() => {
    if (selections?.isEnd) return;
    setFilter(prevState => ({
      ...prevState,
      skip: prevState.skip + PAGE_SIZE,
    }));
  }, [selections]);

  const { HEADER_LIST } = useTableHeader(changeOrder);

  const generateTask = () => {
    openGenerateModal &&
      generationTask(openGenerateModal.id)
        .then(() => setOpenGenerateModal(null))
        .catch(log);
  };

  const changeBeloing = val => {
    setFilter(prev => ({ ...prev, skip: 0, professionalSelectionListSourceType: val }));
  };

  const showSharedActionIconsSx: SxProps = useMemo(() => getIconStyles(IconBaseColor.DarkGrey), []);
  const showDeleteIconSx: SxProps = useMemo(() => getIconStyles(IconBaseColor.Primary), []);

  const onCancelConfirmOrderCreate = useCallback(() => {
    setConfirmOrdersCreate(null);
  }, []);

  return (
    <>
      <Stack gap="24px" width="100%" position="relative" height="calc(100vh - 116px)" marginBottom="-30px">
        <Stack direction="row" justifyContent="space-between">
          <Typography color="grey.700" variant="head32Bold" lineHeight="36px">
            Selections
          </Typography>
          <RoleKeeper permissions="selections.create">
            <Stack direction="row" gap="24px">
              <Button
                color="secondary"
                variant="contained"
                onClick={addSelection}
                startIcon={<PlusIcon />}
                sx={{ width: '151px' }}
                {...buildDataTestIdAttribute(buildFullDataTestId('button-add-selection'))}
              >
                Add selection
              </Button>
            </Stack>
          </RoleKeeper>
        </Stack>
        <Stack height="calc(100% - 66px)">
          <Stack padding="24px 36px" direction="row" gap="60px" sx={{ backgroundColor: '#FFFFFF' }} border={border}>
            {module === 'PROFESSIONAL' ? (
              <SegmentedControl
                values={belongingValues}
                active={filter.professionalSelectionListSourceType || ''}
                handleClick={changeBeloing}
              />
            ) : null}

            <SearchInput onSearch={onSearch} />
          </Stack>
          <Table
            loading={isFetching}
            data={resultSelections || selections?.data || []}
            rowComponent={{
              component: RowSelection,
              props: {
                setOpenGenerateModal,
                setB2cNotificationData,
                setB2bNotificationData,
                activeNotificationsIds,
                showSharedActionIconsSx,
                showDeleteIconSx,
                canCreateOrders,
                setOpenCreateOrder,
              },
            }}
            headerList={HEADER_LIST}
            gridTemplateColumns={'144fr minmax(0, 548fr) minmax(0, 240px) 110px 120fr 126fr'}
            scrollRest={scrollRest}
            emptyData={
              resultSelections && resultSelections.length === 0 && !isFetching && !debouncedFilter.search ? (
                <EmptySearch text="To create a new selection, click the “Add selection” button." />
              ) : undefined
            }
            dataTestId={buildFullDataTestId('selections-table')}
          />
        </Stack>
      </Stack>
      <AttentionModal
        open={!!openGenerateModal}
        setIsOpen={setOpenGenerateModal}
        handleApply={generateTask}
        applyText="Generate"
        body={`You are about to generate a data file for the "${openGenerateModal?.name}" selection. This could take some extended time depending on the criteria complexity. You will receive a notification when the file is ready to download.`}
      />
      {b2cNotificationData && canCreateB2CNotification && (
        <B2cNotificationModal b2cNotificationData={b2cNotificationData} setOpen={setB2cNotificationData} />
      )}
      {b2bNotificationData && canCreateB2BNotification && (
        <B2bNotificationModal b2bNotificationData={b2bNotificationData} setOpen={setB2bNotificationData} />
      )}
      {openCreateOrder && (
        <LoadingFormProvider>
          <OrdersCreateModal
            data={openCreateOrder}
            setOpen={setOpenCreateOrder}
            setConfirmOrdersCreate={setConfirmOrdersCreate}
          />
        </LoadingFormProvider>
      )}
      {confirmOrdersCreate && (
        <ConfirmOrdersCreate ordersData={confirmOrdersCreate} onCancel={onCancelConfirmOrderCreate} />
      )}
    </>
  );
};
